import z from "zod";
import { ProjectBlockerStatus, ProjectBlockerReason, ProjectBlockerAssetType, ProjectBlockerKind } from "../enums";

export const ProjectBlockerReasonSchema = z.nativeEnum(ProjectBlockerReason);

export const ProjectBlockerStatusSchema = z.nativeEnum(ProjectBlockerStatus);

export const ProjectBlockerKindSchema = z.nativeEnum(ProjectBlockerKind);

export const ProjectBlockerAssetTypeSchema = z.nativeEnum(ProjectBlockerAssetType);

export const ProjectBlockerAssetSchema = z.object({
  id: z.number(),
  createdAt: z.string().datetime(),
  assetType: ProjectBlockerAssetTypeSchema,
  signedUrl: z.string(),
  filename: z.string(),
  canDelete: z.boolean(),
  user: z.object({
    id: z.number(),
    fullName: z.string(),
    avatar: z.object({
      thumb: z.string(),
      preview: z.string(),
      large: z.string(),
      retina: z.string(),
    }),
    avatarUrls: z.object({
      thumb: z.string(),
      preview: z.string(),
      large: z.string(),
      retina: z.string(),
    }),
  }),
});

export const MessageUserSchema = z.object({
  avatar: z.object({
    large: z.string(),
    preview: z.string(),
    retina: z.string(),
    thumb: z.string(),
  }),
  email: z.string(),
  firstName: z.string(),
  fullName: z.string(),
  id: z.number(),
  lastName: z.string(),
  phone: z.string(),
});

export const BlockerTicketSchema = z.object({
  archived: z.boolean(),
  assignedAt: z.string().nullable(),
  assignedToCustomerCare: z.boolean(),
  author: MessageUserSchema,
  body: z.string(),
  canArchive: z.boolean(),
  canAssign: z.boolean(),
  canClose: z.boolean(),
  canDelete: z.boolean(),
  canEdit: z.boolean(),
  canReopen: z.boolean(),
  canReply: z.boolean(),
  id: z.number(),
  kind: z.string(),
  parentId: z.number().nullable(),
  resolved: z.boolean(),
  resolvedAt: z.string().nullable(),
  respondedAt: z.string().nullable(),
  starred: z.boolean(),
  subjectLine: z.string().nullable(),
  taggedRoles: z.array(z.number()),
  taggedUsers: z.array(z.number()),
  updatedAt: z.string().nullable(),
  userTagged: z.boolean().nullable(),
  viewed: z.boolean(),
  zendeskStatus: z.string().nullable(),
  zendeskTicketId: z.number(),
});

export const BaseProjectBlockerSchema = z.object({
  id: z.number(),
  title: z.string(),
  zendeskTicketId: z.number().nullable(),
  reason: ProjectBlockerReasonSchema,
  uploadNeeded: z.boolean(),
  status: ProjectBlockerStatusSchema,
  createdAt: z.string().datetime(),
  updatedAt: z.string().datetime(),
  kind: ProjectBlockerKindSchema,
});

export const IndexProjectBlockerSchema = BaseProjectBlockerSchema.extend({
  messageCount: z.number(),
});

export const SiteSurveyContactInfoSchema = z.object({
  homeowner: z.object({
    id: z.number(),
    fullName: z.string(),
    phone: z.string(),
    email: z.string(),
    address: z.object({
      street: z.string(),
      unit: z.string().nullable(),
      city: z.string(),
      state: z.string(),
      zip: z.string(),
    }),
  }),
  installer: z.object({
    id: z.number(),
    companyName: z.string(),
    contactName: z.string(),
    phone: z.string(),
    email: z.string(),
  }),
});

export const DesignAnswersInfoSchema = z.object({
  optimization: z.enum(["1", "2"]).nullable(),
  streetFacing: z.enum(["true", "false"]).nullable(),
  additional: z.string().nullable(),
  installationNotes: z.string().nullable(),
});

const reasonDiscriminantAttrs = {
  contactInfo: z.undefined(),
  designAnswers: z.undefined(),
};

export const ReasonDiscriminant = z.discriminatedUnion("reason", [
  z.object({
    reason: z.literal(ProjectBlockerReason.OS),
    ...reasonDiscriminantAttrs,
  }),
  z.object({
    reason: z.literal(ProjectBlockerReason.MISSING_HAND_OFF_NOTES),
    ...reasonDiscriminantAttrs,
    designAnswers: DesignAnswersInfoSchema,
  }),
  z.object({
    reason: z.literal(ProjectBlockerReason.CHANGE_ORDER_REQUIRED),
    ...reasonDiscriminantAttrs,
  }),
  z.object({
    reason: z.literal(ProjectBlockerReason.BUILDABLE_WITH_CHANGES),
    ...reasonDiscriminantAttrs,
  }),
  z.object({
    reason: z.literal(ProjectBlockerReason.SCHEDULE_SITE_SURVEY),
    ...reasonDiscriminantAttrs,
    contactInfo: SiteSurveyContactInfoSchema,
  }),
]);

export const ProjectBlockerSchema = BaseProjectBlockerSchema.extend({
  additionalInfoTitle: z.string().nullable(),
  additionalInfoUrl: z.string().nullable(),
  assets: z.array(ProjectBlockerAssetSchema).optional(),
  assignedEntity: z.object({ assignedTeamId: z.number() }).optional(),
  description: z.string().optional(),
  messages: z.array(BlockerTicketSchema),
  osChecklistId: z.number().nullable(),
  topic: z.string().nullable(),
  topicId: z.number().optional(),
}).and(ReasonDiscriminant);

export const ProjectBlockersSchema = z.array(IndexProjectBlockerSchema);

export const GetProjectBlockerResponseSchema = z.object({
  status: z.number(),
  body: ProjectBlockerSchema,
});

export const GetProjectBlockersResponseSchema = z.object({
  status: z.number(),
  body: z.object({
    blockers: ProjectBlockersSchema,
    pendingZdTicketCount: z.number(),
    completedBlockers: z.number(),
    allBlockersComplete: z.boolean(),
  }),
});

export const CreateMessageMutationParamsSchema = z.object({
  blockerId: z.number(),
  projectId: z.number(),
  params: z.object({
    assignedTeamId: z.number(),
    assignedToCustomerCare: z.boolean(),
    body: z.string(),
    messageTopicId: z.number(),
    subjectLine: z.string(),
  }),
});

export const CreateAssetMutationParamsSchema = z.object({
  projectId: z.number(),
  params: z.object({
    assignedTeamId: z.number().optional(),
    blockerId: z.number(),
    body: z.string(),
    fileUrls: z.array(z.string()),
    messageTopicId: z.number().optional(),
    subjectLine: z.string().optional(),
  }),
});

export const UpdateDesignAnswersMutationParamsSchema = z.object({
  projectId: z.number(),
  blockerId: z.number(),
  isEnterprise: z.boolean().optional(),
  isAdmin: z.boolean(),
  params: z.object({
    providerLead: z.object({
      designAnswers: z.object({
        optimization: z.enum(["1", "2"]),
        streetFacing: z.enum(["true", "false"]),
        additional: z.string().optional(),
      }),
      installationNotes: z.string(),
    }),
  }),
});

export const BlockerDrawerFooterDiscriminant = z.discriminatedUnion("showFooter", [
  z.object({
    showFooter: z.literal(true),
    submitAction: z.function().args().returns(z.void()),
    buttonText: z.string(),
  }),
  z.object({
    showFooter: z.literal(false),
    submitAction: z.undefined(),
    buttonText: z.undefined(),
  }),
]);
